import { computed, signal } from "@preact/signals";

import * as readerReviewUtils from "readerReviewUtils";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { userProfilePath } from "shared/usernameValidation";
import { htmlEscape } from "shared/utils";

export interface AudioMapPoint {
  t: number;
  w: number;
}

export interface BookOwner {
  _id: string;
  username?: string;
  displayName?: string;
  shortUsername?: string;
  shortDisplayName?: string;
}

export interface UserBookData {
  bookID?: string;
  userID?: string;
  tags?: string[];
  date?: string | Date;
  position?: number | string;
  totalWords?: number | string;
  readTo?: number | string;
  [key: string]: any;
}

export interface HtmlChunkData {
  firstPosition: number;
  lastPosition: number;
  positionIndexes: number[];
  sha1: string;
}

export interface BookMetrics {
  CEF?: string;
  CEFNumber?: number;
  words?: number;
  characters?: number;
  difficultyString?: string;
  [key: string]: any;
}

export interface BookData {
  _id: string;
  audioMap?: AudioMapPoint[];
  author?: string;
  audioFileID?: string;
  attributionHtml?: string;
  category?: string;
  description?: string;
  firstShareDate?: string | Date;
  firstVoterID?: string;
  firstVoterUsername?: string;
  hide?: boolean;
  htmlChunks?: HtmlChunkData[];
  imageURL?: string;
  language?: string;
  metrics?: BookMetrics;
  /** From /api/book/:id, for a public text: whom to report or block. */
  owner?: BookOwner | null;
  ownerID?: string | BookOwner;
  public?: boolean;
  sections?: any[];
  status?: string;
  title?: string;
  totalVotes?: number;
  totalWords?: number;
  url?: string;
  userBook?: UserBookData;
  yourVote?: number;
  [key: string]: any;
}

export type BookViewData = BookData & {
  attributionExists?: boolean;
  authorUrlSortKey?: string;
  daysAgo?: number;
  difficulty?: number;
  downVoted?: boolean;
  haveFrequencies?: boolean;
  importedDate?: Date;
  importedDateString?: string;
  importedString?: string;
  inBookshelf?: boolean;
  languageString?: string;
  percentage?: number;
  readDate?: Date | string;
  readDateString?: string;
  readTo?: number;
  shortTitle?: string;
  showVotes?: boolean;
  titleHint?: string;
  titleLowerCase?: string;
  upVoted?: boolean;
  urlDomain?: string;
  userIsOwner?: boolean;
};

const DEFAULT_AUDIO_MAP: AudioMapPoint[] = [
  { t: 0, w: 0 },
  { t: 312, w: 498 },
];

/**
 * A title as shown: without the marks of a markdown heading it was copied
 * from ("# El cuaderno"), keeping a hashtag or number ("#163 …")
 */
export const displayTitle = (title: string) => title.replace(/^#{1,6}\s+/, "");

const copyBookData = (book: BookData): BookData => ({
  ...book,
  ...(book.title !== undefined && { title: displayTitle(book.title) }),
  audioMap: book.audioMap
    ? book.audioMap.map((point) => ({ ...point }))
    : DEFAULT_AUDIO_MAP.map((point) => ({ ...point })),
  htmlChunks: book.htmlChunks?.map((chunk) => ({
    ...chunk,
    positionIndexes: [...chunk.positionIndexes],
  })),
  metrics: book.metrics ? { ...book.metrics } : undefined,
  userBook: book.userBook
    ? {
        ...book.userBook,
        tags: book.userBook.tags ? [...book.userBook.tags] : undefined,
      }
    : undefined,
});

const ownerIDValue = (ownerID: BookData["ownerID"]): string | undefined => {
  if (!ownerID) {
    return undefined;
  }
  return typeof ownerID === "string" ? ownerID : ownerID._id;
};

export const isUserTheBookOwner = (book: BookData): boolean => {
  const userID = book.userBook?.userID;
  return !!userID && userID === ownerIDValue(book.ownerID);
};

export const hasBookField = (book: BookData, field: string): boolean =>
  book[field] !== undefined && book[field] !== null;

export const bookViewJSON = (
  bookData: BookData,
  userID: string,
  publicMode: boolean
): BookViewData => {
  const book = copyBookData(bookData) as BookViewData;

  if (book.status === "notAvailable") {
    book.shortTitle = book.title = "Un-shared or deleted by owner";
    book.attributionHtml = "N/A";
    return book;
  }

  if (book.language && languageMap.map[book.language]) {
    book.languageString = languageMap.map[book.language].name;
    if (languageMap.map[book.language].frequencies) {
      book.haveFrequencies = true;
    }
  }

  if (!book.title) {
    book.title = "Uh oh... no title!";
  }

  book.titleLowerCase = book.title.toLowerCase();

  if (book.title.length > 80) {
    book.shortTitle = book.title.substring(0, 78) + "...";
    book.titleHint = book.title;
  } else {
    book.shortTitle = book.title;
  }

  book.authorUrlSortKey = "";
  if (book.url) {
    book.urlDomain = book.url.split("//")[1]?.split("/")[0];
    if (book.urlDomain?.length > 30) {
      book.urlDomain = book.urlDomain.substring(0, 28) + "...";
    }

    book.authorUrlSortKey += book.url;
  }
  if (book.url === "") {
    delete book.url;
  }
  if (book.author === "") {
    delete book.author;
  }
  if (book.author) {
    book.authorUrlSortKey += book.author;
  }

  book.importedDate = new Date(parseInt(book._id.substring(0, 8), 16) * 1000);

  if (publicMode && book.firstShareDate) {
    book.daysAgo = Math.round(
      (new Date().getTime() - new Date(book.firstShareDate).getTime()) /
        (24 * 3600 * 1000)
    );
    book.importedDateString = new Date(book.firstShareDate).toUTCString();
  } else {
    book.daysAgo = Math.round(
      (new Date().getTime() - book.importedDate.getTime()) / (24 * 3600 * 1000)
    );
    book.importedDateString = book.importedDate.toUTCString();
  }

  if (book.daysAgo === 0) {
    book.importedString = "today";
  } else if (book.daysAgo === 1) {
    book.importedString = "1 day ago";
  } else {
    book.importedString = book.daysAgo + " days ago";
  }

  if (book.userBook) {
    book.inBookshelf = true;
    book.readDate = book.userBook.date;
    if (book.readDate) {
      book.readDate = new Date(book.readDate);
      book.readDateString = book.readDate.toLocaleDateString();
    }
    if (book.totalWords) {
      book.percentage = Math.round(
        (100 *
          Math.min(
            Number(book.userBook.position),
            Number(book.userBook.totalWords)
          )) /
          book.totalWords
      );
      if ("readTo" in book.userBook) {
        book.readTo = Math.min(
          100,
          parseInt(String(book.userBook.readTo), 10) === 0
            ? 0
            : Math.round(
                (100 * parseInt(String(book.userBook.readTo), 10) + 1) /
                  book.totalWords
              )
        );
      } else {
        book.readTo = Math.min(
          100,
          parseInt(String(book.userBook.position), 10) === 0
            ? 0
            : Math.round(
                (100 * parseInt(String(book.userBook.position), 10) + 1) /
                  book.totalWords
              )
        );
      }
    }
  }

  if (book.url) {
    book.attributionHtml =
      '<span title="' + book.url + '">' + book.urlDomain + "</span>";
  } else if (book.author) {
    book.attributionHtml = '<span title="Author">' + book.author + "</span>";
  }

  if (book.firstVoterID && book.firstVoterUsername) {
    book.ownerID = {
      _id: book.firstVoterID,
      username: book.firstVoterUsername,
      displayName: book.firstVoterUsername,
    };
  }

  if (book.ownerID && userID === ownerIDValue(book.ownerID)) {
    book.userIsOwner = true;
  }

  if (
    book.ownerID &&
    typeof book.ownerID !== "string" &&
    book.ownerID.username &&
    (publicMode || book.userIsOwner)
  ) {
    // Attribute shared texts to the owner's unique username (not their
    // non-unique display name), linking to their profile (by username once
    // migrated, else by id — see userProfilePath).
    const ownerUsername = htmlEscape(book.ownerID.username);
    const ownerPath = htmlEscape(
      userProfilePath(book.ownerID.username, String(book.ownerID._id))
    );
    if (book.attributionHtml) {
      book.attributionHtml += ", via ";
    } else {
      book.attributionHtml = "via ";
    }
    book.attributionHtml +=
      '<span class="mobileOnly">' +
      ownerUsername +
      "</span>" +
      '<a class="noMobile" href="' +
      ownerPath +
      '">' +
      ownerUsername +
      "</a>";
  }

  if (book.attributionHtml && book.attributionHtml.length > 0) {
    book.attributionExists = true;
  }

  if (book.metrics) {
    book.difficulty = book.metrics.CEFNumber;
    book.metrics.difficultyString = {
      A1: "Beginner",
      A2: "Upper Beginner",
      B1: "Intermediate",
      B2: "Upper Intermediate",
      C1: "Advanced",
      C2: "Very Advanced",
    }[book.metrics.CEF ?? ""];
  }

  if (book.imageURL) {
    book.imageURL =
      "/api/getImage/" + encodeURIComponent(book.imageURL) + "/100x";
  }

  if ("totalVotes" in book) {
    book.showVotes = true;

    if ((book.yourVote ?? 0) > 0) {
      book.upVoted = true;
    } else if ((book.yourVote ?? 0) < 0) {
      book.downVoted = true;
    }
  }

  return book;
};

/**
 * The latest change to a book's reading progress, as read: for lists of books
 * (their own BookStores) to show it
 */
export const readingProgress = signal<{
  bookID: string;
  userBook: UserBookData;
} | null>(null);

export default class BookStore {
  readonly book = signal<BookData>(copyBookData({ _id: "" }));
  readonly tags = computed(() => this.book.value.userBook?.tags ?? []);

  constructor(book: BookData) {
    this.book.value = copyBookData(book);
  }

  get id() {
    return this.book.value._id;
  }

  async fetch() {
    const { data } = await readlangClient.request<BookData>(
      "GET",
      "/api/book/" + this.id,
      {}
    );
    this.book.value = copyBookData(data);
  }

  async fetchField<FieldName extends keyof BookData>(field: FieldName) {
    const { data } = await readlangClient.request<BookData[FieldName]>(
      "GET",
      "/api/book/" + this.id + "/" + String(field),
      {}
    );

    this.patchLocal({ [field]: data } as Partial<BookData>);
    return data;
  }

  async patchBook(patch: Partial<BookData>) {
    this.patchLocal(patch);
    await readlangClient.request("PATCH", "/api/book/" + this.id, patch);
  }

  async destroyBook() {
    await readlangClient.request("DELETE", "/api/book/" + this.id, {});
  }

  hasAudio() {
    return Boolean(this.book.value.audioFileID);
  }

  async deleteAudio() {
    await readlangClient.request(
      "DELETE",
      "/api/book/" + this.id + "/audio",
      {}
    );
    this.patchLocal({ audioFileID: undefined, audioMap: undefined });
  }

  async addTag(tagName: string, options: { hide?: boolean } = {}) {
    const { data } = await readlangClient.request<{ tags: string[] }>(
      "POST",
      "/api/book/" + this.id + "/tag",
      { tagName }
    );

    const userBook = {
      ...(this.book.value.userBook ?? { bookID: this.id }),
      tags: data.tags,
    };
    const changes: Partial<BookData> = { userBook };
    if (this.isUserTheOwner()) {
      if (tagName === "shared") {
        changes.public = true;
      }
      if (options.hide !== undefined) {
        changes.hide = options.hide;
      }
      await this.patchBook(changes);
    } else {
      this.patchLocal(changes);
    }
  }

  async removeTag(tagName: string) {
    const { data } = await readlangClient.request<{ tags: string[] }>(
      "DELETE",
      "/api/book/" + this.id + "/tag",
      { tagName }
    );

    const userBook = {
      ...(this.book.value.userBook ?? { bookID: this.id }),
      tags: data.tags,
    };
    const changes: Partial<BookData> = { userBook };
    if (this.isUserTheOwner() && tagName === "shared") {
      changes.public = false;
      changes.hide = false;
      await this.patchBook(changes);
    } else {
      this.patchLocal(changes);
    }
  }

  /** The reading progress as it's changed through another BookStore */
  showUserBook(userBook: UserBookData) {
    this.patchLocal({ userBook });
  }

  hasTag(tagName: string) {
    return this.tags.value.includes(tagName);
  }

  isUserTheOwner() {
    return isUserTheBookOwner(this.book.value);
  }

  async patchUserBook(userBookPatch: Partial<UserBookData>) {
    const userBook = {
      ...(this.book.value.userBook ?? { bookID: this.id }),
      ...userBookPatch,
    };
    this.patchLocal({ userBook });
    readingProgress.value = { bookID: this.id, userBook };
    await readlangClient.request(
      "PATCH",
      "/api/book/" + this.id + "/userBook",
      userBookPatch
    );
  }

  /**
   * Fetches the HTML chunk after the one holding a position into the browser's
   * cache, so that turning the page into it needn't wait for the network
   */
  prefetchChunkAfter(position: number) {
    const nextChunk = (this.book.value.htmlChunks ?? []).find(
      (chunk) => chunk.firstPosition > position
    );
    if (!nextChunk) {
      return;
    }
    // The URL getHtmlFragment requests (cacheable for an hour), but not through
    // readlangClient, whose error bar would report a failure that only matters
    // if the page turn's own request fails too. Read to the end, to be cached.
    void fetch(`/api/chunk/${nextChunk.sha1}`, { credentials: "include" })
      .then((response) => response.text())
      .catch(() => {});
  }

  async getHtmlFragment(
    fromPosition: number,
    toPositionInclusive: number,
    addReviewButton?: boolean
  ) {
    let html = "";
    const toPosition = toPositionInclusive + 1;
    const chunksToFetch: { index: number; sha1: string }[] = [];
    const htmlChunks = this.book.value.htmlChunks ?? [];

    for (const [index, chunk] of htmlChunks.entries()) {
      let fetch = false;

      if (chunk.firstPosition > toPosition) {
        break;
      }

      if (
        toPosition >= chunk.firstPosition &&
        fromPosition <= chunk.lastPosition
      ) {
        fetch = true;
      }
      if (
        fromPosition <= chunk.lastPosition &&
        toPosition >= chunk.firstPosition
      ) {
        fetch = true;
      }
      if (fetch) {
        chunksToFetch.push({ index, sha1: chunk.sha1 });
      }
    }

    for (const chunkToFetch of chunksToFetch) {
      const chunk = htmlChunks[chunkToFetch.index];
      let fromOffset: number | undefined;
      let toOffset: number | undefined;
      let fetchedChunk: string | null = null;

      if (chunk.firstPosition > toPosition) {
        break;
      }

      const fetchChunk = async (sha1: string) => {
        if (fetchedChunk) {
          return fetchedChunk;
        }

        // Errors are handled in the catch (rather than an errorCallback) so
        // the awaited promise always settles — with an errorCallback, domain
        // (4xx) failures leave it pending forever, hanging getHtmlFragment.
        try {
          const { data } = await readlangClient.request<string>(
            "GET",
            `/api/chunk/${sha1}`,
            {},
            undefined,
            undefined,
            { cache: true, dataType: "text" }
          );

          fetchedChunk = data;
        } catch (error: any) {
          console.error("ERROR NO CHUNK", error);
          const { status, alreadyNotified } = error?.jqXHR ?? {};
          if (alreadyNotified) {
            // Terminal failure (network loss, 5xx): readlangClient already
            // notified — don't blow away the page content, and rethrow so
            // getHtmlFragment rejects rather than fulfilling with empty or
            // partial content (which would blank the reader page or feed an
            // empty text to AI summaries).
            throw error;
          } else if (status === 404) {
            if (/regenerated/.test(window.location.hash)) {
              $("#entirePage").text("Can't regenerate again");
            } else {
              $("#entirePage").html("Regenerating HTML...");

              try {
                await readlangClient.request("GET", `/api/book/${this.id}`, {
                  forceRegenerate: true,
                });
                window.location.hash = "regenerated";
                window.location.reload();
              } catch (regenerateError) {
                console.error("Error regenerating book HTML:", regenerateError);
                $("#entirePage").html("Error regenerating book HTML");
              }
            }
          } else {
            $("#entirePage").html("Unknown error fetching book HTML");
          }
        }

        return fetchedChunk;
      };

      if (fromPosition <= chunk.lastPosition) {
        if (fromPosition <= chunk.firstPosition) {
          fromOffset = 0;
        } else {
          const chunkData = await fetchChunk(chunk.sha1);
          if (!chunkData) {
            continue;
          }

          const fromOffsetIndex =
            Math.floor((fromPosition - chunk.firstPosition) / 8) - 1;
          fromOffset = 0;

          if (fromOffsetIndex >= 0) {
            fromOffset = chunk.positionIndexes[fromOffsetIndex];
          }

          fromOffset = chunkData.indexOf("<span data-p=" + fromPosition + ">");
          while (fromOffset > 0 && !/>| /.test(chunkData[fromOffset - 1])) {
            fromOffset--;
          }
        }
      }

      if (
        toPosition < chunk.firstPosition ||
        fromPosition > chunk.lastPosition
      ) {
        // skip this chunk
      } else {
        const chunkData = await fetchChunk(chunk.sha1);
        if (!chunkData) {
          continue;
        }

        if (toPosition <= chunk.lastPosition) {
          const toOffsetIndex =
            Math.floor((toPosition - chunk.firstPosition) / 8) - 1;
          toOffset = 0;

          if (toOffsetIndex >= 0) {
            toOffset = chunk.positionIndexes[toOffsetIndex];
          }
          toOffset = chunkData.indexOf("<span data-p=" + toPosition + ">");
        } else {
          toOffset = chunkData.length;
        }
      }
      if (
        fromPosition <= chunk.lastPosition &&
        toPosition >= chunk.firstPosition
      ) {
        const chunkData = await fetchChunk(chunk.sha1);
        if (
          chunkData &&
          typeof fromOffset !== "undefined" &&
          typeof toOffset !== "undefined"
        ) {
          html += chunkData.substring(fromOffset, toOffset);
        }
      }
    }

    if (addReviewButton) {
      html = await readerReviewUtils.spliceInReviewSessionButtons(
        this.id,
        html,
        toPosition,
        this.book.value.totalWords,
        this.book.value.sections
      );
    }

    return html;
  }

  async vote(vote: number) {
    const { data } = await readlangClient.request<{
      totalVotes: number;
      yourVote: number;
    }>("POST", "/api/book/" + this.id + "/vote", { vote });

    this.patchLocal({
      totalVotes: data.totalVotes,
      yourVote: data.yourVote,
    });
  }

  viewJSON(userID: string, publicMode: boolean) {
    return bookViewJSON(this.book.value, userID, publicMode);
  }

  patchLocal(patch: Partial<BookData>) {
    this.book.value = copyBookData({
      ...this.book.value,
      ...patch,
    });
  }
}
